import React, { FC, useState } from 'react';
import Icon from '../Icons/Icon';
import '/public/css/components/shareSocialButtons.css';
import Link from 'components/AppSafeLink/AppSafeLink';

interface Props {
  contentUrl: string;
  title: string;
  id?: string;
  shareText?: string;
  contentType?: string;
}

interface dataLayerObjType {
  event: string;
  eventCategory: string;
  eventAction: string;
  eventLabel: boolean;
  eventInteraction: string;
}

const ShareSocialButtons: FC<Props> = ({
  shareText = 'Share',
  contentUrl,
  title,
  contentType = 'page',
}) => {
  //Don't send the share event information to GA by default
  const fullUrl = `https://www.mprnews.org/${contentUrl}`;
  const shareFBEventGA = 'facebook';
  const shareTwitterEventGA = 'tweet';
  const dataLayer: any[] = [];
  const shareEventGA = false;
  const twitterLink = `https://twitter.com/share?url=${fullUrl}&text=${encodeURIComponent(
    title
  )}`;
  const fbLink = `https://www.facebook.com/sharer.php?u=${fullUrl}`;
  const liLink = `https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent(
    title
  )}%20${fullUrl}`;
  const blueSkyLink = `https://bsky.app/intent/compose?text=${encodeURIComponent(
    title
  )}%20${fullUrl}`;
  const body = `Check out this ${contentType} from MPR News: ${fullUrl}`;
  const mailLink = `mailto:?subject=${encodeURIComponent(
    title
  )}&body=${encodeURIComponent(body)}`;
  const [copied, setCopied] = useState<boolean>(false);

  const dataLayerObj = {
    event: 'GAevent',
    eventCategory: 'share',
    eventAction: 'click',
    eventLabel: shareEventGA,
    eventInteraction: 'false',
  };

  const pushLayerInfoForGA = (
    dataLayerObj: dataLayerObjType,
    shareEventGA: string
  ) => {
    if (shareEventGA && dataLayer) {
      try {
        //push data layer through Google Tag in GTM, data lives in GA
        dataLayer.push(dataLayerObj);
      } catch (e) {
        return;
      }
    }
  };

  const copyUrl = () => {
    navigator.clipboard.writeText(fullUrl);
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
    }, 3000);
  };

  return (
    <>
      <div className="share-social-outer">
        <details className="share-social">
          <summary className="hdg-small share-hdg">{shareText}</summary>
          <div className="share-social-details">
            <button className="share-social-link" onClick={copyUrl}>
              <img src="/img/icon-link.svg" alt="Copy link" />
              {copied && (
                <output className="share-social-copied">Link copied!</output>
              )}
            </button>
            {/* Twitter class needs to stay, it triggers GA tracking */}
            <Link
              href={twitterLink}
              target="_blank"
              rel="noreferrer"
              aria-label="Share on X (twitter.com) - link opens in a new tab"
              className="share-social-twitter twitter"
              onClick={() => {
                pushLayerInfoForGA(dataLayerObj, shareTwitterEventGA);
              }}
            >
              <Icon name="twitter" />
            </Link>
            {/* Facebook class needs to stay, it triggers GA tracking */}
            <Link
              href={fbLink}
              target="_blank"
              rel="noreferrer"
              aria-label="Share on Facebook - link opens in a new tab"
              className="share-social-facebook facebook"
              onClick={() => {
                pushLayerInfoForGA(dataLayerObj, shareFBEventGA);
              }}
            >
              <Icon name="facebook" />
            </Link>
            <Link
              href={liLink}
              target="_blank"
              rel="noreferrer"
              aria-label="Share on LinkedIn - link opens in a new tab"
              className="share-social-linkedin"
            >
              <img
                src="/img/icon-linkedin.svg"
                alt="" /* already has aria-label */
              />
            </Link>
            <Link
              href={blueSkyLink}
              target="_blank"
              rel="noreferrer"
              aria-label="Share on BlueSky - link opens in a new tab"
              className="share-social-bluesky"
            >
              <img
                src="/img/icon-bluesky.svg"
                alt="" /* already has aria-label */
              />
            </Link>
            <Link
              href={mailLink}
              target="_blank"
              rel="noreferrer"
              aria-label="Share via email - link opens in a new tab"
              className="share-social-email email"
            >
              <Icon name="mail" />
            </Link>
          </div>
        </details>
      </div>
    </>
  );
};

export default ShareSocialButtons;
